import React, { useState, useEffect, useLayoutEffect } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import CircularProgress from "@mui/material/CircularProgress";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

import messageBlue from "../../assets/images/icons/messageBlue.png";
import progressIcon from "../../assets/images/icons/progressPink.png";
import messageGreen from "../../assets/images/icons/messagebGreen.png";

import Cards from "../../Views/Menu/Cards";
import NewOverview from "../../Views/Overview/NewOverview";

import News from "../../Views/News/News";
import TechnicalAnalysis from "../../Views/TechnicalAnalysis/TechnicalAnalysis";
import useWindowDimemsions from "../../hooks/useWindowDimesnions";

import { identifyTweet } from "../../Actions/TweetsList";
import { identifyNews } from "../../Actions/NewsList";
import { headerTypes, TabPanelProps } from "./menu.types";
import "./menu.scss";
import FinancialInsights from "../../Views/FinancialInsights/FinancialInsights";
import MutualFunds from "../../Views/MutualFunds/MutualFunds";
import MenuMarquee from "./MenuMarquee";

import backArrow from "../../assets/images/icons/back_arrow.svg";
import { useNavigate } from "react-router-dom";

/**
 * Represents a tab panel used within the Menu component.
 *
 * @param {TabPanelProps} props - The props for the TabPanel component.
 * @returns {JSX.Element} The JSX representation of the TabPanel.
 */
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ display: value !== index ? "none" : "block" }}
      {...other}
    >
      <Box sx={{ p: 3 }}>
        <span>{children}</span>
      </Box>
    </div>
  );
}
/**
 * Helper function to generate accessibility props for a tab.
 *
 * @param {number} index - The index of the tab.
 * @returns {Object} Accessibility props for the tab.
 */
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
/**
 * This React component represents the main menu of the application.
 *
 * The main menu allows users to navigate between different sections of the application,
 * including Overview, Technical Analysis, News, Tweets, Company Docs, and Knowledge Graph.
 *
 * This function is called in \src\Router\Router.tsx
 *
 * @returns {JSX.Element} The JSX representation of the Menu component.
 */
const Menu = () => {
  let { ticker: tickerID, contentID, newsid: newsID, tweetID } = useParams();

  let location = useLocation();
  function useQuery() {
    const { search } = useLocation();

    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();

  const tickerQuery = query.get("ticker");
  const tickerId = tickerQuery
    ? tickerQuery
    : tickerID
      ? tickerID
      : window.localStorage.getItem("tickerId");

  const [value, setValue] = React.useState(0);

  const navigateMenu = useSelector((state: any) => state.NavigateMenu);

  useLayoutEffect(() => {
    setValue(0);
  }, [navigateMenu]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const tabsRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroll = () => {
      const scroller = tabsRef.current?.querySelector(".MuiTabs-scroller");
      if (scroller) {
        if (value === 3 || value === 4) {
          scroller.scrollTo({ left: scroller.scrollWidth, behavior: "smooth" });
        } else if (value === 0 || value === 1) {
          scroller.scrollTo({ left: 0, behavior: "smooth" });
        }
      }
    };
    
    scroll();
    const t1 = setTimeout(scroll, 50);
    const t2 = setTimeout(scroll, 150);
    const t3 = setTimeout(scroll, 300);
    const t4 = setTimeout(scroll, 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [value]);

  const [headerInf, setHeaderInf] = useState<headerTypes>({} as headerTypes);
  const [isLoaderVisible, setIsLoaderVisible] = useState<boolean>(false);
  const [tradePrice, setTradePrice] = useState<number | string | null>(null);
  const [isTradePriceLoading, setIsTradePriceLoading] = useState<boolean>(false);
  const [tradePriceError, setTradePriceError] = useState<string | null>(null);
  
  // Stock data state for fetching prev_close, open, high, and current price
  const [stockData, setStockData] = useState<any>(null);
  const [isStockDataLoading, setIsStockDataLoading] = useState<boolean>(false);
  const [stockDataError, setStockDataError] = useState<string | null>(null);
  
  const dispatch = useDispatch();

  //Redirecting routes to the respective tabs
  const getHeaderData = () => {
    let tickerId = tickerQuery
      ? tickerQuery
      : tickerID
        ? tickerID
        : window.localStorage.getItem("tickerId");
    if (location.pathname.toLowerCase().includes("news")) {
      setValue(2);
    } else if (location.pathname.toLowerCase().includes("tweets")) {
      setValue(3);
    } else if (location.pathname.toLowerCase().includes("technicalanalysis")) {
      setValue(1);
    }

    setIsLoaderVisible(true);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/${tickerId}/header_information.json`,
      )
      .then(
        (response) => {
          setIsLoaderVisible(false);
          setHeaderInf(response.data);
        },
        (error) => {
          setIsLoaderVisible(false);
          console.log(error);
        },
      );
  };

  useEffect(() => {
    getHeaderData();
  }, [tickerId]);

  useEffect(() => {
    if (!tickerId) {
      setTradePrice(null);
      setTradePriceError(null);
      return;
    }

    const symbolWithSuffix = tickerId;
    const symbolWithoutSuffix = tickerId.includes(".") ? tickerId.split(".")[0] : tickerId;
    const tradingSymbols = Array.from(
      new Set([symbolWithSuffix, symbolWithoutSuffix].filter(Boolean)),
    );
    const baseUrl = process.env.REACT_APP_TRADE_URL;

    if (!baseUrl) {
      setTradePrice(null);
      setTradePriceError("Missing REACT_APP_TRADE_URL");
      return;
    }

    let isCancelled = false;

    const fetchPrice = async () => {
      setIsTradePriceLoading(true);
      setTradePrice(null);
      setTradePriceError(null);

      const authToken =
        window.localStorage.getItem("accessToken") ||
        window.localStorage.getItem("token") ||
        window.localStorage.getItem("jwt");

      for (const symbol of tradingSymbols) {
        try {
          const url = `${baseUrl}/data/price/${encodeURIComponent(symbol)}`;
          console.log("[trade-price] fetching", { tickerId, symbol, url });
          const response = await axios.get(url, {
            withCredentials: true,
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          });
          const data = response?.data;
          const price =
            data?.price ??
            data?.data?.price ??
            data?.ltp ??
            data?.data?.ltp ??
            data;

          if (isCancelled) return;
          if (price !== null && price !== undefined && price !== "") {
            setTradePrice(price);
            setIsTradePriceLoading(false);
            return;
          }
        } catch (error: any) {
          if (isCancelled) return;
          console.log("[trade-price] fetch failed", { tickerId, symbol, error });
          const status = error?.response?.status;
          setTradePriceError(
            status === 401
              ? "Unauthorized (missing auth token/cookie)"
              : error?.response?.data?.message ||
                  error?.message ||
                  "Failed to fetch price",
          );
        }
      }

      if (!isCancelled) {
        setIsTradePriceLoading(false);
        setTradePrice(null);
      }
    };

    fetchPrice();

    return () => {
      isCancelled = true;
    };
  }, [tickerId]);

  // Fetch stock data (prev_close, open, high, current price) from stock_data.json
  useEffect(() => {
    if (!tickerId) {
      setStockData(null);
      setStockDataError(null);
      return;
    }

    let isCancelled = false;
    const baseUrl = process.env.REACT_APP_COMPANY_DOC;

    if (!baseUrl) {
      setStockDataError("Missing REACT_APP_COMPANY_DOC environment variable");
      return;
    }

    const fetchStockData = async () => {
      setIsStockDataLoading(true);
      setStockData(null);
      setStockDataError(null);

      try {
        const url = `${baseUrl}/website_v2/stock_data.json`;
        console.log("[stock-data] fetching from", url);
        
        const response = await axios.get(url);
        
        if (isCancelled) return;

        const stockDataArray = response?.data?.data || [];
        
        // Find matching stock by ticker
        const matchedStock = stockDataArray.find(
          (stock: any) => stock.ticker === tickerId
        );

        if (matchedStock) {
          const extractedData = {
            prev_close: matchedStock.prev_close,
            open: matchedStock.open,
            high: matchedStock.high,
            current_price: matchedStock.ltp,
          };
          
          console.log("[stock-data] matched stock found", {
            ticker: tickerId,
            data: extractedData,
          });
          
          setStockData(extractedData);
          setIsStockDataLoading(false);
        } else {
          if (!isCancelled) {
            setStockDataError(`Stock ticker "${tickerId}" not found in data`);
            setIsStockDataLoading(false);
          }
        }
      } catch (error: any) {
        if (isCancelled) return;
        console.log("[stock-data] fetch failed", { tickerId, error });
        setStockDataError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch stock data"
        );
        setIsStockDataLoading(false);
      }
    };

    fetchStockData();

    return () => {
      isCancelled = true;
    };
  }, [tickerId]);

  useEffect(() => {
    if (value !== 3) {
      dispatch(identifyNews([]));
    }
    // if (value !== 4)
    else {
      dispatch(identifyTweet([]));
    }
  }, [value]);

  const { width }: any = useWindowDimemsions();
  const lgWidth = 991;

  const bankNameStyle =
    width && width > lgWidth
      ? { fontSize: "1.5rem" } // large screen
      : { fontSize: "1.0rem" }; // small screen

  const financeStyle =
    width && width > lgWidth
      ? { fontSize: "10px" } // large screen
      : { fontSize: "8px" }; // small screen

  const tabFontSize =
    width && width >= lgWidth
      ? { fontSize: "15px" }
      : width && width >= 685 && width < lgWidth
        ? { fontSize: "14px" }
        : { fontSize: "13px" };

  const formatValue = (value: string | number) => {
    if (!value || value === "Data not Present") return "NA";

    const num = Number(value);
    if (isNaN(num)) return value;

    // Example formatting: 1,23,456.78 (Indian format)
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const arry = ["/search", "/detail", "/aggregator"];
  const navigate = useNavigate();

  return (
    <div style={{ background: "#ffffff" }}>
      <div className="main-menu">
        <div className="main-menu-inner">
          <div className="menu-header-row">
            <div className="menu-header-left">
              <div className="header" />
              <div className="main-menu-header">
                <div className="stock-logo-wrapper">
                  <img
                    src={`${process.env.REACT_APP_COMPANY_DOC}/Marquee_logos/${
                      tickerId?.split(".")[0]
                    }.svg`}
                    alt="logo"
                    className="stock-logo"
                  />
                </div>
                <div className="stock-meta">
                  <h4 className="bank-name" style={bankNameStyle}>
                    {headerInf.Stock_Name}
                  </h4>
                  <span className="finance" style={financeStyle}>
                    {headerInf.Stock_Sector}
                  </span>
                </div>
              </div>
            </div>

            <div className="menu-header-right">
              {(() => {
                const currentPriceNum =
                  stockData?.current_price !== null && stockData?.current_price !== undefined
                    ? stockData.current_price
                    : tradePrice ?? null;
                const prevCloseNum =
                  stockData?.prev_close !== null && stockData?.prev_close !== undefined
                    ? stockData.prev_close
                    : null;
                const change =
                  currentPriceNum !== null && prevCloseNum !== null
                    ? currentPriceNum - prevCloseNum
                    : null;
                const changePct =
                  change !== null && prevCloseNum !== null && prevCloseNum !== 0
                    ? (change / prevCloseNum) * 100
                    : null;
                const isUp = change !== null ? change >= 0 : null;

                const currentPriceDisplay = isStockDataLoading || isTradePriceLoading
                  ? "Loading..."
                  : currentPriceNum !== null
                    ? formatValue(currentPriceNum)
                    : "NA";

                return (
                  <div className="stock-price-stats">
                    <div className="sps-secondary">
                      <div className="sps-item">
                        <span className="sps-label">PREV CLOSE</span>
                        <span className="sps-value">
                          {stockData?.prev_close !== null && stockData?.prev_close !== undefined
                            ? formatValue(stockData.prev_close)
                            : headerInf.Yesterdays_Close === "Data not Present"
                              ? "NA"
                              : formatValue(headerInf.Yesterdays_Close)}
                        </span>
                      </div>
                      <div className="sps-divider" />
                      <div className="sps-item">
                        <span className="sps-label">OPEN</span>
                        <span className="sps-value">
                          {stockData?.open !== null && stockData?.open !== undefined
                            ? formatValue(stockData.open)
                            : headerInf.Todays_Open === "Data not Present"
                              ? "NA"
                              : formatValue(headerInf.Todays_Open)}
                        </span>
                      </div>
                      <div className="sps-divider" />
                      <div className="sps-item">
                        <span className="sps-label">DAY HIGH</span>
                        <span className="sps-value sps-value--up">
                          {stockData?.high !== null && stockData?.high !== undefined
                            ? formatValue(stockData.high)
                            : headerInf.Todays_Close === "Data not Present"
                              ? "NA"
                              : formatValue(headerInf.Todays_Close)}
                        </span>
                      </div>
                    </div>

                    <div className="sps-current-divider" />

                    <div className="sps-current">
                      <span className="sps-label">CURRENT PRICE</span>
                      <span className="sps-current-price">{currentPriceDisplay}</span>
                      {change !== null && changePct !== null && (
                        <span className={`sps-change ${isUp ? "sps-change--up" : "sps-change--down"}`}>
                          {isUp
                            ? <TrendingUp size={16} strokeWidth={2.5} />
                            : <TrendingDown size={16} strokeWidth={2.5} />
                          }
                          {Math.abs(change).toFixed(2)}&ensp;
                          <span className="sps-change-pct">
                            ({isUp ? "+" : "-"}{Math.abs(changePct).toFixed(2)}%)
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <MenuMarquee
            text1={" Previous Close : " + headerInf.Yesterdays_Close}
            text2={" Open : " + headerInf.Todays_Open}
            text3={" Last Traded Price : " + headerInf.Todays_Close}
            text4={
              " News Last Updated : " +
              moment(headerInf.Last_Updated).format("DD-MMM-YYYY HH:mm ")
            }
            text5={
              " Stocks Last Updated : " +
              moment(headerInf.Last_Updated_Stocks).format("DD-MMM-YYYY HH:mm ")
            }
          />

          <div className="menu-header-divider" />
        </div>

        <Box sx={{ width: "100%" }}>
          <div className="tab-shell">
            <AppBar position="static" color="default" sx={{ background: "white", boxShadow: "none" }}>
              <Tabs
                ref={tabsRef}
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="menu tabs"
              >
                <Tab className="Tab-custom-style" label="Overview"          {...a11yProps(0)} />
                <Tab className="Tab-custom-style" label="Technical Analysis" {...a11yProps(1)} />
                <Tab className="Tab-custom-style" label="News"               {...a11yProps(2)} />
                <Tab className="Tab-custom-style" label="Company Reports"    {...a11yProps(4)} />
                <Tab className="Tab-custom-style" label="Mutual Funds"       {...a11yProps(5)} />
              </Tabs>
            </AppBar>
          </div>
          <TabPanel value={value} index={0}>
            <NewOverview
              activeTab={setValue}
              stock_name={headerInf.Stock_Name}
            />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <TechnicalAnalysis />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <News tickerid={tickerID || ""} news_id={parseInt(newsID || "")} />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <FinancialInsights />
          </TabPanel>
          <TabPanel value={value} index={4}>
            <MutualFunds />
          </TabPanel>
        </Box>

        {isLoaderVisible && (
          <div className="loader">
            {" "}
            <CircularProgress />
          </div>
        )}
      </div>
    </div>
  );
};

export default Menu;
